import React from 'react'
import PropTypes from 'prop-types'

import Icon from 'Pods/Shared/Icon/Icon'
import Tooltip from 'Pods/Shared/Tooltip'

const priceLimits = {
  min: 2,
  printableMin: 3,
  max: 10000,
}

const titleLimits = {
  min: 5,
  max: 64,
}

class InlineEdit extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      active: false,
      defaultValue: this.props.value,
      value: this.props.value,
      error: null,
      inputErrorClass: '',
    }

    this.toggleEditing = this.toggleEditing.bind(this)
    this.onSubmit = this.onSubmit.bind(this)
    this.onCancel = this.onCancel.bind(this)
    this.handleInputChange = this.handleInputChange.bind(this)
    this.formatTitle = this.formatTitle.bind(this)
  }

  onSubmit(event) {
    event.preventDefault()

    const payload = { data: { attributes: {} } }

    payload.data.attributes[this.props.attribute] = this.state.value

    axios.patch(`/api/internal/items/${this.props.modelId}`, payload)
      .then(() => this.setState({ active: false, defaultValue: this.state.value }))
      .catch((xhr, status, err) => this.setState({ error: xhr.response.data.errors[0].detail }))
  }

  onCancel(event) {
    event.preventDefault()

    this.setState({
      active: false, value: this.state.defaultValue, error: null, inputErrorClass: '',
    })
  }

  handleInputChange(event, action) {
    event.preventDefault()
    const inputErrorClass = this.inputClass(event.target.value, action)

    this.setState({ value: event.target.value, error: null, inputErrorClass })

    this.showError(action, event.target.value)
  }

  inputClass(value, action) {
    const minPrice = this.props.isPrintable ? priceLimits.printableMin : priceLimits.min

    if (action === 'price-change' && (value < minPrice || value > priceLimits.max)) {
      return 'is-invalid'
    } if (action === 'title-change' && (value.length < titleLimits.min || value.length > titleLimits.max)) {
      return 'is-invalid'
    }
    return ''
  }

  fieldValidationMessage(message) {
    return I18n.t(`profile.my_products.errors.${message}`)
  }

  showError(action, value) {
    const minPrice = this.props.isPrintable ? priceLimits.printableMin : priceLimits.min
    let message = null

    if (action === 'price-change') {
      if (!value) {
        message = 'empty'
      } else if (value < minPrice) {
        message = this.props.isPrintable ? 'min_3dp_fixed' : 'min_fixed'
      } else if (value > priceLimits.max) {
        message = 'max_fixed'
      }
    } else if (action === 'title-change') {
      if (value.length === 0) {
        message = 'empty'
      } else if (value.length < titleLimits.min) {
        message = 'min_title'
      } else if (value.length > titleLimits.max) {
        message = 'max_title'
      }
    }

    this.setState({ error: message ? this.fieldValidationMessage(message) : message })
  }

  toggleEditing() {
    this.setState({ active: !this.state.active })
  }

  formatTitle(title, id) {
    if (title && title.length > 64) {
      return (
        <Tooltip
          id={`title-tooltip-${id}`}
          tooltipClassName="tooltip--big"
          content={title}
        >
          {title.substr(0, 64)}...
        </Tooltip>
      )
    }
    return title
  }

  textValue() {
    if (this.props.attribute === 'price') {
      return `$${this.state.value}`
    }
    const title = this.state.value ? this.formatTitle(this.state.value, this.props.modelId) : <span className="no-title">Title is not set</span>

    return (
      this.props.isCollection && this.props.isDraft ? (
        <span className="collection-draft-trigger" onClick={() => this.props.editCollection(this.props.modelId)}>
          {title}
        </span>
      ) : (
        <a href={this.props.url} target="_blank">
          {title}
        </a>
      )
    )
  }

  inlineForm() {
    const error = <div className="error-message--tooltip">{this.state.error}</div>
    return (
      <div className={`inline-edit ${this.state.active ? 'is-active' : ''}`}>
        <form onSubmit={this.onSubmit}>
          {this.props.attribute === 'price' ? (
            <div className="form-group form-group--with-prefix">
              {this.state.error && error}
              <div className="form-group-prepend">
                <span className={`form-group-text ${this.state.inputErrorClass}`}>$</span>
              </div>
              <input
                type="number"
                min={this.props.isPrintable ? priceLimits.printableMin : priceLimits.min}
                max={priceLimits.max}
                className={`form-control ${this.state.inputErrorClass}`}
                value={this.state.value}
                onChange={(event) => this.handleInputChange(event, 'price-change')}
                step="0.01"
                lang="en"
              />
            </div>
          ) : (
            <div className="form-group">
              {this.state.error && error}
              <input
                type="text"
                value={this.state.value}
                onChange={(event) => this.handleInputChange(event, 'title-change')}
                className={`form-control ${this.state.inputErrorClass}`}
              />
            </div>
          )}

          <div className="row">
            <div className="col-6">
              <button onClick={this.onCancel} className="btn btn-fat btn-outline-default btn-block">Cancel</button>
            </div>
            <div className="col-6">
              <button
                type="submit"
                className="btn btn-fat btn-primary btn-block"
                disabled={this.state.inputErrorClass !== ''}
              >Confirm
              </button>
            </div>
          </div>
        </form>
      </div>
    )
  }

  render() {
    return (
      this.state.active
        ? this.inlineForm()
        : (
          <span>
            <button onClick={this.toggleEditing} className="btn btn-default btn-transparent inline-edit__edit-button">
              <Icon type={['fas', 'edit']} size="xs" />
            </button> {this.textValue()}
          </span>
        )
    )
  }
}

InlineEdit.propTypes = {
  modelId: PropTypes.string.isRequired,
  attribute: PropTypes.string.isRequired,
  value: PropTypes.any.isRequired,
  url: PropTypes.string,
  isCollection: PropTypes.bool,
  isDraft: PropTypes.bool,
  isPrintable: PropTypes.bool,
  editCollection: PropTypes.func,
}

InlineEdit.defaultProps = {
  url: null,
  isCollection: false,
  isDraft: false,
  isPrintable: false,
  editCollection: () => {},
}

export default InlineEdit
